import { useRef, ReactNode, forwardRef, ForwardedRef, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import Link from '@/components/Link';
import Submenu, { SubmenuBlock, SubmenuButtons, SubmenuColumns, SubmenuList, useToggleSubmenu } from '../Submenu';
import analytics from '@/helpers/analytics';

import { headerAtom } from '@/store/common';

import styles from './header-menu.module.css';

interface ButtonElementProps {
  href?: string,
  className?: string,
  onClick: () => void,
  children: ReactNode,
}

const ButtonElement = forwardRef(({ href, className, onClick, children }: ButtonElementProps, ref: ForwardedRef<HTMLElement>) => {
  if (href) return (
    <Link
      href={href}
      className={className}
      onClick={onClick}
      ref={ref}
    >
      {children}
    </Link>
  );

  return (
    <div
      className={className}
      onClick={onClick}
      ref={ref as ForwardedRef<HTMLDivElement>}
    >
      {children}
    </div>
  );
});

ButtonElement.displayName = 'ButtonElement';

interface MenuItemProps {
  name: string,
  href?: string,
  analyticsId?: string,
  submenuWidth?: string,
  onClick?: () => void,
  children?: ReactNode,
}

const HeaderMenuItem = ({ name, href, analyticsId, onClick, children, submenuWidth }: MenuItemProps) => {
  const { t } = useTranslation();
  const { pathname } = useRouter();

  const buttonRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { open: isOpenSubmenu } = useToggleSubmenu({ buttonRef, containerRef });

  const isActive = href && (pathname.startsWith(href) || pathname.startsWith('/[locale]' + href));
  const buttonPosition = useMemo(() => {
    if (!buttonRef.current) return '0px';
    return Math.max(0, buttonRef.current.offsetLeft + buttonRef.current.offsetWidth) + 'px';
    // eslint-disable-next-line
  }, [buttonRef.current]);

  return (
    <div className={styles.item}>
      <ButtonElement
        href={href}
        className={classNames(
          styles.link,
          isOpenSubmenu && styles.open,
          isActive && styles.active,
          !!children && styles.withSubmenu
        )}
        onClick={() => {
          if (onClick) onClick();
          if (analyticsId) analytics.trackYandex(analyticsId);
        }}
        ref={buttonRef}
      >
        {t(name)}
        {!!children && (
          <ReactSVG src="/icons/chevron-down.svg" className={styles.linkArrow} wrapper="span" />
        )}
      </ButtonElement>
      {!!children && (
        <Submenu
          open={isOpenSubmenu}
          width={submenuWidth}
          position={buttonPosition || undefined}
          ref={containerRef}
        >
          {children}
        </Submenu>
      )}
    </div>
  );
};

interface HeaderMenuProps {
  className?: string
}

const HeaderMenu = ({ className }: HeaderMenuProps) => {
  const { solutions, templates, categories, integrations, useful } = useAtomValue(headerAtom);

  return (
    <nav className={classNames(styles.links, className)}>
      <HeaderMenuItem
        name="constructor"
        submenuWidth="100%"
      >
        <div className={styles.grid}>
          <SubmenuBlock title="Для кого" className={styles.solutions}>
            <SubmenuList items={solutions} />
          </SubmenuBlock>
          <SubmenuBlock title="Подпись" className={styles.sign} href="/sign">
            Подписание документов с помощью ПЭП и УКЭП для бизнеса
            <ReactSVG src="/images/menu-stroke.svg" className={styles.signBg} />
          </SubmenuBlock>
          <SubmenuBlock title="Готовые шаблоны" className={styles.templates}>
            <SubmenuColumns templates={templates} categories={categories} />
          </SubmenuBlock>
        </div>
      </HeaderMenuItem>
      <HeaderMenuItem
        name="tariffs"
        href="/tariffs"
        analyticsId="tarif_header"
      />
      <HeaderMenuItem
        name="integrations"
        submenuWidth="344px"
      >
        <SubmenuButtons items={[
          ...integrations.map((item) => ({ ...item, type: 'image' as const })),
          {
            id: 'integrations-all',
            name: <span className={styles.integrationsAll}>Смотреть все виджеты <ReactSVG src="/icons/chevron-right.svg" wrapper="span" /></span>,
            href: '/marketplace',
          }
        ]} />
      </HeaderMenuItem>
      <HeaderMenuItem
        name="useful"
        submenuWidth="260px"
      >
        <SubmenuButtons items={useful} />
      </HeaderMenuItem>
    </nav>
  );
};

export default HeaderMenu;
